import { Text as RNText, type TextProps as RNTextProps, type TextStyle } from "react-native";
import { StyleSheet, UnistylesRuntime } from "react-native-unistyles";
import type { AppColorPalette, AppColorPaletteShades, AppSpacing } from "@src/unistyles";
import { AppFontFamily } from "@src/providers/fonts-provider";

type TextColorPalette = AppColorPalette | AppColorPaletteShades;

export type TextSize =
  | "xxs/regular"
  | "xxs/medium"
  | "xxs/semibold"
  | "xxs/bold"
  | "xs/regular"
  | "xs/medium"
  | "xs/semibold"
  | "xs/bold"
  | "sm/regular"
  | "sm/medium"
  | "sm/semibold"
  | "sm/bold"
  | "md/regular"
  | "md/medium"
  | "md/semibold"
  | "md/bold"
  | "lg/regular"
  | "lg/medium"
  | "lg/semibold"
  | "lg/bold"
  | "xl"
  | "2xl"
  | "3xl"
  | "4xl"
  | "5xl"
  | "6xl"
  | "7xl"
  | "h1"
  | "h1/medium"
  | "h1/regular"
  | "h1/semibold"
  | "h2"
  | "h2/medium"
  | "h2/regular"
  | "h2/semibold"
  | "h3"
  | "h3/medium"
  | "h3/regular"
  | "h3/semibold"
  | "h4"
  | "h4/medium"
  | "h4/regular"
  | "h4/semibold"
  | "display/xl"
  | "display/lg"
  | "display/md";

export type TextProps = RNTextProps & {
  color?: TextColorPalette;
  fontSize?: TextStyle["fontSize"];
  fontWeight?: TextStyle["fontWeight"];
  textAlign?: TextStyle["textAlign"];
  lineHeight?: TextStyle["lineHeight"];
  letterSpacing?: TextStyle["letterSpacing"];
  textTransform?: TextStyle["textTransform"];
  textDecorationLine?: TextStyle["textDecorationLine"];
  fontStyle?: TextStyle["fontStyle"];
  textStyle?: TextSize;
  truncate?: boolean;
  lineClamp?: number;
  p?: AppSpacing;
  padding?: AppSpacing;
  px?: AppSpacing;
  paddingX?: AppSpacing;
  py?: AppSpacing;
  paddingY?: AppSpacing;
  m?: AppSpacing;
  margin?: AppSpacing;
  mx?: AppSpacing;
  marginX?: AppSpacing;
  my?: AppSpacing;
  marginY?: AppSpacing;
  mb?: AppSpacing;
  marginBottom?: AppSpacing;
  mt?: AppSpacing;
  marginTop?: AppSpacing;
};

export function Text({
  style,
  color,
  fontSize,
  fontWeight,
  textAlign,
  lineHeight,
  letterSpacing,
  textTransform,
  textDecorationLine,
  fontStyle,
  textStyle,
  truncate,
  lineClamp,
  p,
  padding,
  px,
  paddingX,
  py,
  paddingY,
  m,
  margin,
  mx,
  marginX,
  my,
  marginY,
  mb,
  marginBottom,
  mt,
  marginTop,
  ...rest
}: TextProps) {
  return (
    <RNText
      style={[
        styles({
          color,
          fontSize,
          fontWeight,
          textAlign,
          lineHeight,
          letterSpacing,
          textTransform,
          textDecorationLine,
          fontStyle,
          textStyle,
          truncate,
          lineClamp,
          p: p ?? padding,
          px: px ?? paddingX,
          py: py ?? paddingY,
          m: m ?? margin,
          mx: mx ?? marginX,
          my: my ?? marginY,
          mb: mb ?? marginBottom,
          mt: mt ?? marginTop,
        }).text,
        style,
      ]}
      numberOfLines={lineClamp}
      ellipsizeMode={truncate || lineClamp ? "tail" : undefined}
      {...rest}
    />
  );
}

export { Text as ThemedText };

const getTextColor = (colorProp?: TextColorPalette): string => {
  const theme = UnistylesRuntime.getTheme();
  const isDarkMode = UnistylesRuntime.themeName === "dark";

  if (!colorProp) {
    return theme.colors.text as string;
  }

  // Handle white and black specially - return the default value, not a shade
  if (colorProp === "white") {
    return theme.colors.white.default;
  }
  if (colorProp === "black") {
    return theme.colors.black.default;
  }

  const color = theme.colors[colorProp];

  if (typeof color === "string") {
    return color;
  }

  if (typeof color === "object" && color !== null) {
    const shade = isDarkMode ? 300 : 700;
    return (color as Record<number, string>)[shade] || (theme.colors.text as string);
  }

  return theme.colors.text as string;
};

const styles = (props: {
  color?: TextColorPalette;
  fontSize?: TextStyle["fontSize"];
  fontWeight?: TextStyle["fontWeight"];
  textAlign?: TextStyle["textAlign"];
  lineHeight?: TextStyle["lineHeight"];
  letterSpacing?: TextStyle["letterSpacing"];
  textTransform?: TextStyle["textTransform"];
  textDecorationLine?: TextStyle["textDecorationLine"];
  fontStyle?: TextStyle["fontStyle"];
  textStyle?: TextSize;
  truncate?: boolean;
  lineClamp?: number;
  p?: AppSpacing;
  px?: AppSpacing;
  py?: AppSpacing;
  m?: AppSpacing;
  mx?: AppSpacing;
  my?: AppSpacing;
  mb?: AppSpacing;
  mt?: AppSpacing;
}) =>
  StyleSheet.create((theme) => {
    const textStyles: Record<
      TextSize,
      {
        fontSize: TextStyle["fontSize"];
        lineHeight: TextStyle["lineHeight"];
        fontFamily?: string;
        fontWeight?: TextStyle["fontWeight"];
      }
    > = {
      "xxs/regular": { fontSize: 10, lineHeight: 12, fontFamily: AppFontFamily["Inter-Regular"], fontWeight: 400 },
      "xxs/medium": { fontSize: 10, lineHeight: 12, fontFamily: AppFontFamily["Inter-Medium"], fontWeight: 500 },
      "xxs/semibold": { fontSize: 10, lineHeight: 12, fontFamily: AppFontFamily["Inter-SemiBold"], fontWeight: 600 },
      "xxs/bold": { fontSize: 10, lineHeight: 12, fontFamily: AppFontFamily["Inter-Bold"], fontWeight: 700 },
      "xs/regular": { fontSize: 12, lineHeight: 16, fontFamily: AppFontFamily["Inter-Regular"], fontWeight: 400 },
      "xs/medium": { fontSize: 12, lineHeight: 16, fontFamily: AppFontFamily["Inter-Medium"], fontWeight: 500 },
      "xs/semibold": { fontSize: 12, lineHeight: 16, fontFamily: AppFontFamily["Inter-SemiBold"], fontWeight: 600 },
      "xs/bold": { fontSize: 12, lineHeight: 16, fontFamily: AppFontFamily["Inter-Bold"], fontWeight: 700 },
      "sm/regular": { fontSize: 14, lineHeight: 16, fontFamily: AppFontFamily["Inter-Regular"], fontWeight: 400 },
      "sm/medium": { fontSize: 14, lineHeight: 16, fontFamily: AppFontFamily["Inter-Medium"], fontWeight: 500 },
      "sm/semibold": { fontSize: 14, lineHeight: 16, fontFamily: AppFontFamily["Inter-SemiBold"], fontWeight: 600 },
      "sm/bold": { fontSize: 14, lineHeight: 16, fontFamily: AppFontFamily["Inter-Bold"], fontWeight: 700 },
      "md/regular": { fontSize: 16, lineHeight: 24, fontFamily: AppFontFamily["Inter-Regular"], fontWeight: 400 },
      "md/medium": { fontSize: 16, lineHeight: 24, fontFamily: AppFontFamily["Inter-Medium"], fontWeight: 500 },
      "md/semibold": { fontSize: 16, lineHeight: 24, fontFamily: AppFontFamily["Inter-SemiBold"], fontWeight: 600 },
      "md/bold": { fontSize: 16, lineHeight: 24, fontFamily: AppFontFamily["Inter-Bold"], fontWeight: 700 },
      "lg/regular": { fontSize: 18, lineHeight: 24, fontFamily: AppFontFamily["Inter-Regular"], fontWeight: 400 },
      "lg/medium": { fontSize: 18, lineHeight: 24, fontFamily: AppFontFamily["Inter-Medium"], fontWeight: 500 },
      "lg/semibold": { fontSize: 18, lineHeight: 24, fontFamily: AppFontFamily["Inter-SemiBold"], fontWeight: 600 },
      "lg/bold": { fontSize: 18, lineHeight: 24, fontFamily: AppFontFamily["Inter-Bold"], fontWeight: 700 },
      xl: { fontSize: 20, lineHeight: 28 },
      "2xl": { fontSize: 24, lineHeight: 32 },
      "3xl": { fontSize: 30, lineHeight: 36 },
      "4xl": { fontSize: 36, lineHeight: 40 },
      "5xl": { fontSize: 48, lineHeight: 48 },
      "6xl": { fontSize: 60, lineHeight: 60 },
      "7xl": { fontSize: 72, lineHeight: 72 },
      h1: { fontSize: 32, lineHeight: 40, fontFamily: AppFontFamily["Inter-Bold"], fontWeight: 700 },
      "h1/medium": { fontSize: 32, lineHeight: 40, fontFamily: AppFontFamily["Inter-Medium"], fontWeight: 500 },
      "h1/regular": { fontSize: 32, lineHeight: 40, fontFamily: AppFontFamily["Inter-Regular"], fontWeight: 400 },
      "h1/semibold": { fontSize: 32, lineHeight: 40, fontFamily: AppFontFamily["Inter-SemiBold"], fontWeight: 600 },
      h2: { fontSize: 28, lineHeight: 36, fontFamily: AppFontFamily["Inter-Bold"], fontWeight: 700 },
      "h2/medium": { fontSize: 28, lineHeight: 36, fontFamily: AppFontFamily["Inter-Medium"], fontWeight: 500 },
      "h2/regular": { fontSize: 28, lineHeight: 36, fontFamily: AppFontFamily["Inter-Regular"], fontWeight: 400 },
      "h2/semibold": { fontSize: 28, lineHeight: 36, fontFamily: AppFontFamily["Inter-SemiBold"], fontWeight: 600 },
      h3: { fontSize: 24, lineHeight: 32, fontFamily: AppFontFamily["Inter-Bold"], fontWeight: 700 },
      "h3/medium": { fontSize: 24, lineHeight: 32, fontFamily: AppFontFamily["Inter-Medium"], fontWeight: 500 },
      "h3/regular": { fontSize: 24, lineHeight: 32, fontFamily: AppFontFamily["Inter-Regular"], fontWeight: 400 },
      "h3/semibold": { fontSize: 24, lineHeight: 32, fontFamily: AppFontFamily["Inter-SemiBold"], fontWeight: 600 },
      h4: { fontSize: 20, lineHeight: 28, fontFamily: AppFontFamily["Inter-Bold"], fontWeight: 700 },
      "h4/medium": { fontSize: 20, lineHeight: 28, fontFamily: AppFontFamily["Inter-Medium"], fontWeight: 500 },
      "h4/regular": { fontSize: 20, lineHeight: 28, fontFamily: AppFontFamily["Inter-Regular"], fontWeight: 400 },
      "h4/semibold": { fontSize: 20, lineHeight: 28, fontFamily: AppFontFamily["Inter-SemiBold"], fontWeight: 600 },
      "display/xl": { fontSize: 64, lineHeight: 72 },
      "display/lg": { fontSize: 48, lineHeight: 56 },
      "display/md": { fontSize: 40, lineHeight: 48 },
    };

    return {
      text: {
        color: getTextColor(props.color),
        fontSize: props.fontSize ?? (props.textStyle ? textStyles?.[props.textStyle]?.fontSize : 16),
        fontWeight: props.fontWeight ?? (props.textStyle ? textStyles?.[props.textStyle]?.fontWeight : 400),
        textAlign: props.textAlign,
        lineHeight: props.lineHeight ?? (props.textStyle ? textStyles?.[props.textStyle]?.lineHeight : 24),
        letterSpacing: props.letterSpacing,
        textTransform: props.textTransform,
        textDecorationLine: props.textDecorationLine,
        fontStyle: props.fontStyle,
        padding: props.p ? theme.spacing[props.p] : undefined,
        paddingHorizontal: props.px ? theme.spacing[props.px] : undefined,
        paddingVertical: props.py ? theme.spacing[props.py] : undefined,
        margin: props.m ? theme.spacing[props.m] : undefined,
        marginHorizontal: props.mx ? theme.spacing[props.mx] : undefined,
        marginVertical: props.my ? theme.spacing[props.my] : undefined,
        marginBottom: props.mb ? theme.spacing[props.mb] : undefined,
        marginTop: props.mt ? theme.spacing[props.mt] : undefined,
      },
    };
  });
